import { CLIENT_LIFECYCLE, LIFECYCLE_LABEL, type CampaignDetail } from '@cyberscore/contracts';
import { CalendarClock, Loader2, Plus } from 'lucide-react';
import { useDeferredValue, useMemo, useState } from 'react';
import { ApiError } from '../../lib/api';
import { href } from '../../lib/router';
import { useSession, can } from '../../lib/session';
import { Drawer } from '../../ui/Overlay';
import { useToast } from '../../ui/Toasts';
import { STATUS_LABEL, STATUS_TONE } from '../assessments/labels';
import { day } from '../clients/ClientRecord';
import { when } from '../clients/parts';
import { useCampaign, useCampaignChanges, useCampaigns, usePreview } from './api';

const pct = (n: number, d: number) => (d ? `${Math.round((n / d) * 100)}%` : '–');

/**
 * Renewal campaigns (task 69): renewal checks for many clients at once, each carrying the last answers
 * forward for the business to confirm; progress across the clients; and checks that start by themselves
 * before each renewal.
 */
export function CampaignsPage() {
  const campaigns = useCampaigns();
  const { tenant } = useSession();
  const canRun = can(tenant, 'campaigns.create');
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      <div className="page-head row-between">
        <div>
          <h1>Renewals</h1>
          <p>Start renewal checks for many clients at once. Each business sees last year’s answers and confirms what is still true.</p>
        </div>
        {canRun && <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}><Plus size={15} /> New campaign</button>}
      </div>
      <section className="card">
        {campaigns.isPending ? <div className="empty">Loading…</div> : !campaigns.data?.length ? (
          <div className="empty"><CalendarClock size={28} /><strong>No campaigns yet</strong>Pick the clients renewing soon and start their checks together.</div>
        ) : (
          <div className="table-wrap">
            <table className="grid">
              <thead><tr><th>Campaign</th><th className="right">Checks</th><th className="right">Submitted</th><th className="right wide">Decided</th><th className="right wide">Answers confirmed</th><th className="wide">Starts by itself</th></tr></thead>
              <tbody>
                {campaigns.data.map((c) => (
                  <tr key={c.id} className="clickable" onClick={() => setOpen(c.id)}>
                    <td><button type="button" className="linklike cell-title" onClick={() => setOpen(c.id)}>{c.name}</button>{!c.active && <span className="pill neutral"> Closed</span>}<div className="muted small">{c.created_by_name ?? 'Someone'}, {when(c.created_at)}</div></td>
                    <td className="right num">{c.counts.started}{c.counts.skipped ? <span className="muted small"> · {c.counts.skipped} skipped</span> : null}</td>
                    <td className="right num">{c.counts.submitted} <span className="muted small">{pct(c.counts.submitted, c.counts.started)}</span></td>
                    <td className="right num wide">{c.counts.decided}</td>
                    <td className="right num wide">{c.carry_forward ? `${c.counts.settled} of ${c.counts.carried}` : '–'}</td>
                    <td className="wide small">{c.auto_days ? `${c.auto_days} days before renewal` : <span className="muted">No</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {creating && <NewCampaign onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); setOpen(id); }} />}
      {open && <CampaignDrawer id={open} canRun={canRun} onClose={() => setOpen(null)} />}
    </>
  ); // prettier-ignore
}

function NewCampaign({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const toast = useToast();
  const { create } = useCampaignChanges();
  const year = new Date().getFullYear() + 1;
  const [name, setName] = useState(
    `Renewals ${new Date().toLocaleDateString('en-MY', { month: 'long' })} ${year}`,
  );
  const [renewing, setRenewing] = useState('60');
  const [stage, setStage] = useState('bound');
  const [search, setSearch] = useState('');
  const q = useDeferredValue(search.trim());
  const [carry, setCarry] = useState(true);
  const [auto, setAuto] = useState(false);
  const [autoDays, setAutoDays] = useState('45');
  const [banner, setBanner] = useState<string | null>(null);
  const query = useMemo(() => new URLSearchParams(Object.entries({ renewing, lifecycle: stage, q }).filter(([, v]) => v)).toString(), [renewing, stage, q]); // prettier-ignore
  const preview = usePreview(query);
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const startable = (preview.data ?? []).filter((p) => !p.blocked);
  const chosen = picked ?? new Set(startable.map((p) => p.customer_key));

  const save = async () => {
    setBanner(null);
    try {
      const c = await create.mutateAsync({ name: name.trim(), query, start: [...chosen], carry_forward: carry, auto_days: auto ? Number(autoDays) : null }); // prettier-ignore
      toast(
        `${c.name}: ${c.counts.started} check${c.counts.started === 1 ? '' : 's'} started${c.counts.skipped ? `, ${c.counts.skipped} skipped` : ''}.`,
      );
      onCreated(c.id);
    } catch (e) {
      setBanner(e instanceof ApiError ? e.message : 'Could not start the campaign');
    }
  };

  return (
    <Drawer
      title="New renewal campaign"
      subtitle="Pick clients, then start their renewal checks together."
      onClose={onClose}
      footer={<><span className="grow" /><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" disabled={create.isPending || !name.trim() || (!chosen.size && !auto)} onClick={save}>{create.isPending && <Loader2 size={15} className="spin" />}{chosen.size ? `Start ${chosen.size} check${chosen.size === 1 ? '' : 's'}` : 'Create campaign'}</button></>} // prettier-ignore
    >
      {banner && <div className="result bad" role="alert"><span>{banner}</span></div>}
      <div className="field"><label htmlFor="cp-name">Name</label><input id="cp-name" className="input" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} /><span className="hint">Also the title of each check.</span></div>
      <div className="field"><label htmlFor="cp-search">Only clients named</label><input id="cp-search" className="input" type="search" maxLength={100} placeholder="Name or policy number (optional)" value={search} onChange={(e) => { setSearch(e.target.value); setPicked(null); }} /></div>
      <div className="two">
        <div className="field"><label htmlFor="cp-renewing">Renewing</label>
          <select id="cp-renewing" className="input" value={renewing} onChange={(e) => { setRenewing(e.target.value); setPicked(null); }}>
            <option value="30">Within 30 days</option><option value="60">Within 60 days</option><option value="90">Within 90 days</option><option value="overdue">Renewal passed</option><option value="">Any time</option>
          </select>
        </div>
        <div className="field"><label htmlFor="cp-stage">Stage</label>
          <select id="cp-stage" className="input" value={stage} onChange={(e) => { setStage(e.target.value); setPicked(null); }}>
            <option value="">Any active stage</option>
            {CLIENT_LIFECYCLE.filter((s) => s !== 'lapsed').map((s) => <option key={s} value={s}>{LIFECYCLE_LABEL[s]}</option>)}
          </select>
        </div>
      </div>
      <label className="toggle-row small"><input type="checkbox" checked={carry} onChange={(e) => setCarry(e.target.checked)} /> <span>Carry last answers forward for the business to confirm</span></label>
      <label className="toggle-row small"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> <span>Also start a check by itself for every client like these</span>
        {auto && <><input aria-label="Days before renewal" className="input days-input" type="number" min={7} max={180} value={autoDays} onChange={(e) => setAutoDays(e.target.value)} /> days before its renewal</>}
      </label>
      <div className="eyebrow">{preview.isPending ? 'Finding clients…' : `${preview.data?.length ?? 0} clients match; ${chosen.size} picked`}</div>
      {preview.data && !preview.data.length && <p className="muted small">No clients match. Try a longer renewal window or another stage.{auto ? ' The campaign can still start checks by itself as clients come to match.' : ''}</p>}
      {!!preview.data?.length && <ul className="pick-list" aria-label="Clients">
        {(preview.data ?? []).map((p) => (
          <li key={p.customer_key}>
            <label className="row gap">
              <input type="checkbox" disabled={!!p.blocked} checked={chosen.has(p.customer_key)} onChange={(e) => { const n = new Set(chosen); if (e.target.checked) n.add(p.customer_key); else n.delete(p.customer_key); setPicked(n); }} />
              <span className="minw grow"><b>{p.customer_name}</b><span className="muted small"> · renews {day(p.renewal_date)}</span>{p.blocked && <div className="muted small">{p.blocked}</div>}</span>
            </label>
          </li>
        ))}
      </ul>}
    </Drawer>
  ); // prettier-ignore
}

function CampaignDrawer({ id, canRun, onClose }: { id: string; canRun: boolean; onClose: () => void }) {
  const q = useCampaign(id);
  const { close } = useCampaignChanges();
  const toast = useToast();
  const c: CampaignDetail | undefined = q.data;
  return (
    <Drawer
      title={c?.name ?? 'Campaign'}
      subtitle={c ? `${c.counts.started} checks · ${c.counts.submitted} submitted · ${c.counts.decided} decided${c.auto_days ? ` · starts checks ${c.auto_days} days before renewal` : ''}` : undefined}
      onClose={onClose}
      footer={<>{c?.active && canRun && <button type="button" className="btn" disabled={close.isPending} onClick={async () => { await close.mutateAsync(id); toast('Campaign closed: it starts no more checks.'); }}>Close campaign</button>}<span className="grow" /><button type="button" className="btn" onClick={onClose}>Done</button></>} // prettier-ignore
    >
      {!c ? <div className="empty">Loading…</div> : (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Client</th><th>Check</th><th className="right">Confirmed</th></tr></thead>
            <tbody>
              {c.items.map((i) => (
                <tr key={i.customer_key}>
                  <td><b>{i.customer_name}</b><div className="muted small">renews {day(i.renewal_date)}</div></td>
                  <td>{i.state === 'skipped' ? <><span className="pill neutral">Skipped</span><div className="muted small">{i.reason}</div></> : i.status ? <a href={href.assessment(i.assessment_id!)}><span className={`pill ${STATUS_TONE[i.status]}`}>{STATUS_LABEL[i.status]}</span></a> : null}</td>
                  <td className="right num small">{i.carried ? `${i.settled} of ${i.carried}` : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Drawer>
  ); // prettier-ignore
}
